import { useId, useRef, type KeyboardEvent, type ReactNode } from 'react';
import { Icon, type IconName } from '../icons';

// Tabs: roving tabindex + arrow keys, `aria-controls` panels. Controlled (value/onChange).
// `variant="pills"` is the segmented control for view switchers and small toggles.

export interface TabDef {
  key: string;
  label: ReactNode;
  icon?: IconName;
  count?: number | string;
  disabled?: boolean;
}

export function Tabs({
  tabs,
  value,
  onChange,
  variant = 'line',
  'aria-label': ariaLabel,
  className,
}: {
  tabs: TabDef[];
  value: string;
  onChange: (key: string) => void;
  variant?: 'line' | 'pills';
  'aria-label'?: string;
  className?: string;
}) {
  const id = useId();
  const listRef = useRef<HTMLDivElement>(null);
  const onKey = (e: KeyboardEvent, i: number) => {
    if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft' && e.key !== 'Home' && e.key !== 'End') return;
    e.preventDefault();
    const enabled = tabs.filter((t) => !t.disabled);
    const cur = enabled.findIndex((t) => t.key === tabs[i]!.key);
    let next = cur;
    if (e.key === 'ArrowRight') next = (cur + 1) % enabled.length;
    if (e.key === 'ArrowLeft') next = (cur - 1 + enabled.length) % enabled.length;
    if (e.key === 'Home') next = 0;
    if (e.key === 'End') next = enabled.length - 1;
    const t = enabled[next];
    if (t) {
      onChange(t.key);
      listRef.current?.querySelector<HTMLElement>(`[data-key="${CSS.escape(t.key)}"]`)?.focus();
    }
  };
  return (
    <div ref={listRef} role="tablist" aria-label={ariaLabel} className={['hf-tabs', variant === 'pills' && 'pills', className].filter(Boolean).join(' ')}>
      {tabs.map((t, i) => {
        const selected = t.key === value;
        return (
          <button
            key={t.key}
            type="button"
            role="tab"
            data-key={t.key}
            id={`${id}-${t.key}`}
            aria-selected={selected}
            aria-controls={`${id}-panel-${t.key}`}
            tabIndex={selected ? 0 : -1}
            disabled={t.disabled}
            className="hf-tab"
            onClick={() => onChange(t.key)}
            onKeyDown={(e) => onKey(e, i)}
          >
            {t.icon && <Icon name={t.icon} size={14} />}
            {t.label}
            {t.count != null && <span className="count">{t.count}</span>}
          </button>
        );
      })}
    </div>
  );
}

export function TabPanel({ tabKey, active, children, id }: { tabKey: string; active: boolean; children: ReactNode; id?: string }) {
  if (!active) return null;
  return (
    <div role="tabpanel" id={id ? `${id}-panel-${tabKey}` : undefined} tabIndex={0} style={{ outline: 'none' }}>
      {children}
    </div>
  );
}
